import { Checkbox, Label } from '@parrot-software/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EmployeeDeleteTarget, EmployeeListItem } from '@personnel/presentation/types';
import { EmployeeDetailSheet } from './EmployeeDetailSheet';
import { EmployeeSummaryCard } from './EmployeeSummaryCard';

interface EmployeesMobileListProps {
  employees: EmployeeListItem[];
  selectedEmployees: { uuid: string }[];
  areAllSelected: boolean;
  onToggleAll: (checked: boolean) => void;
  onToggleOne: (employee: EmployeeListItem, checked: boolean) => void;
  onDeleteEmployee?: (employee?: EmployeeDeleteTarget) => void;
}

/**
 * The users list as a stack of cards, for the widths where a seven-column table cannot fit.
 *
 * It keeps the bulk selection the table has: a card carries its own checkbox and the row above the
 * stack ticks them all, so the delete button of the pagination works the same on a phone.
 *
 * @param {EmployeesMobileListProps} props - Component properties
 */
export const EmployeesMobileList = ({
  employees,
  selectedEmployees,
  areAllSelected,
  onToggleAll,
  onToggleOne,
  onDeleteEmployee,
}: EmployeesMobileListProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListItem | null>(null);

  return (
    <div className="flex flex-col gap-3 font-sans">
      <div className="flex items-center gap-2 px-1">
        <Checkbox checked={areAllSelected} id="employees-select-all" onCheckedChange={onToggleAll} />
        <Label className="text-sm text-muted-foreground" htmlFor="employees-select-all">
          {t('list.mobile.selectAll')}
        </Label>
      </div>

      {employees.map((employee) => (
        <EmployeeSummaryCard
          checked={selectedEmployees.some((selected) => selected.uuid === employee.uuid)}
          employee={employee}
          key={employee.uuid}
          onCheckedChange={onToggleOne}
          onClick={setSelectedEmployee}
        />
      ))}

      <EmployeeDetailSheet
        employee={selectedEmployee}
        onDelete={
          onDeleteEmployee &&
          ((target) => {
            setSelectedEmployee(null);
            onDeleteEmployee(target);
          })
        }
        onOpenChange={(open) => {
          if (!open) setSelectedEmployee(null);
        }}
        open={Boolean(selectedEmployee)}
      />
    </div>
  );
};
